import { type FC, useMemo, useState } from "react";
import type { GridCard } from "widgets";
import { Card, CardRowSimple, LoadingSpinner, TriggerButton } from "widgets";

import { useError } from "@/app-utils/graphql/useError.ts";
import { useNotificationSummaryViewUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { AsyncSearchWithExprParser } from "@/ui-modules/core/forms/AsyncSearchWithExprParser.tsx";
import { filterCategorizedResources } from "@/ui-modules/entities/cards/utils/card-utils.ts";
import { useGetPlaybookEventInputCardQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";
import {
  type CategorizedResources,
  PlaybookEventResources,
} from "@/ui-modules/playbooks/events/PlaybookEventResources.tsx";

interface IPlaybookEventInputsCardProps extends GridCard {
  eventId: number;
}

export const PlaybookEventInputsCard: FC<IPlaybookEventInputsCardProps> = ({ eventId }) => {
  const { data, loading, error, refetch } = useGetPlaybookEventInputCardQuery({
    variables: { eventId: eventId },
  });
  void useError(error);
  useNotificationSummaryViewUpdate(refetch, eventId);

  const playbookEvent = data?.playbook?.event;

  const [filter, setFilter] = useState<string | null>(null);
  const [isExpanded, setExpanded] = useState(false);

  const categorizedResources = useMemo<CategorizedResources[]>(() => {
    const resources: CategorizedResources[] = playbookEvent?.inputs
      ?.filter((input) => input?.resources?.length)
      ?.map((eventInput) =>
        eventInput
          ? {
              category: eventInput.label,
              resources: eventInput.resources,
            }
          : null,
      )
      .filter((r) => !!r) as CategorizedResources[];

    return resources && resources.length > 0 ? resources : [];
  }, [playbookEvent?.inputs]);

  const filteredResources = useMemo(() => {
    const filteredCategorizedResources = filterCategorizedResources(filter ?? "", categorizedResources);
    const isExpandable =
      filteredCategorizedResources.length > 1 &&
      filteredCategorizedResources.reduce(
        (previous, currentValue) => previous || (currentValue?.resources?.length ?? 0) > 1,
        false,
      );

    return { filteredCategorizedResources, isExpandable };
  }, [filter, categorizedResources]);

  return (
    <Card
      key="event-input-card"
      title={"Inputs"}
      actions={[
        <AsyncSearchWithExprParser
          onSearchExprChange={async (value) => setFilter(value?.toLowerCase())}
          key={"playbook-event-card-output-search"}
        />,
        filteredResources.isExpandable && (
          <TriggerButton
            onPress={() => setExpanded((e) => !e)}
            icon={isExpanded ? "unfold_less_double" : "unfold_more_double"}
            label={isExpanded ? "Collapse Files of Same Type" : "Expand Stacked Files"}
            key={"playbook-event-card-output-expand-button"}
          />
        ),
      ]}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {!categorizedResources.length ? (
            <ContentPlaceholder>No Inputs</ContentPlaceholder>
          ) : (
            !filteredResources.filteredCategorizedResources.length && (
              <ContentPlaceholder>No input matching your filter</ContentPlaceholder>
            )
          )}
          <CardRowSimple>
            {filteredResources.filteredCategorizedResources.map((categorizedResources, i) => (
              <PlaybookEventResources
                categorizedResources={categorizedResources}
                single={filteredResources.filteredCategorizedResources.length === 1}
                isExpanded={isExpanded}
                key={`playbook-event-card-input${i}`}
              />
            ))}
          </CardRowSimple>
        </>
      )}
    </Card>
  );
};
